<template>
  <el-dialog
    v-el-drag-dialog
    :visible.sync="innerVisible"
    :title="$t('SeaExport.PrintBooking')"
    width="1000px"
    :close-on-click-modal="false"
    append-to-body
    class="app-main-inner"
    @close="closeHandler()"
  >
    <div class="app-main-inner-row">
      <el-form label-width="60px" label-position="left" :inline="true">
        <el-row :gutter="10">
          <el-col :span="$selectByLang(8, 10)">
            <el-form-item :label="$t('queryJob.AirlineCompany')" :label-width="$selectByLang('80px', '100px')">
              {{ form.lbCarrier }}
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="ETD:">
              {{ form.lbETD }}
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="8">
            <el-form-item :label="$t('queryJob.Flight')">
              <el-input v-model="form.txtFlight" no="1" @change="txtFlightChangeHandler" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item :label-width="$selectByLang('80px', '110px')" :label="$t('queryJob.FieldOperation')">
              <cargo-user-select
                no="2"
                :method-params="{category:'role',shape:'siteOp'}"
                :default-value="form.siteOp||''"
                @change="p=>{siteOpChangeHandler(p);}"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-button no="3" type="primary" :disabled="btnSendEnable" @click="sendSiteOp">{{ $t('queryJob.SendFieldOperation') }}</el-button>
          </el-col>
        </el-row>
      </el-form>
    </div>
    <div class="app-main-inner-row row-grid" style="min-height:350px;padding:0">
      <cargo-ag-grid
        :grid-params.sync="gridParams"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :show-side-bar="false"
        :row-data="rowData"
        style="width:100%;height:350px;"
        @gridReady="params=>gridApi=params.api"
      />
    </div>
    <div slot="footer">
      <el-button no="4" type="primary" @click="enterHandler">{{ $t('common.btn.confirm') }}</el-button>
      <el-button no="5" @click="closeHandler">{{ $t('common.btn.cancel') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>

import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import cargoAdvanceSelect from '@/components/Cargo/Common/cargoAdvanceSelect'
import { Company } from '@/class/model/mdm/Company'
import cargoUserSelect from '@/components/Cargo/Common/cargoUserSelect'
import { User } from '@/class/model/sys/User'
import DateUtils from '@/utils/dateUtils'
import { sendSiteOp, sendXCOpEmail } from '@/api/AirExport/airExportJob'
import CargoAgGridCommonInputEditor from '@/components/Cargo/Common/CargoAgGridCommonInputEditor'

export default {
  name: 'BookingPrintWindow',
  components: { cargoAdvanceSelect, cargoUserSelect },
  props: {
    visible: { type: Boolean, required: true },
    jobs: { type: Array, default: () => [] }
  },
  data() {
    return {
      selectTypeNames: TYPE_NAMES,
      form: {
        carrierId: 0,
        lbCarrier: '',
        lbETD: '',
        txtFlight: '',
        siteOp: 0,
        siteOpDepartment: 0
      },
      innerVisible: false,
      rowData: [],
      gridParams: {},
      gridApi: null,
      gridOptions: {
        rowSelection: 'multiple',
        suppressCellSelection: true,
        suppressRowClickSelection: false,
        singleClickEdit: true,
        frameworkComponents: {
          CargoAgGridCommonInputEditor
        }
      },
      savedPrint: false,
      isEdited: false
    }
  },
  computed: {
    btnSendEnable() {
      return !this.form.siteOp > 0
    },
    columnDefs() {
      return [
        { headerName: this.$t('Payment.businessNumber'), field: 'jobNo', width: 180, colId: 'jobNo' },
        { headerName: this.$t('Payment.mbl'), field: 'mbl', width: 180, colId: 'mbl' },
        { headerName: this.$t('Payment.portOfDestination'), field: 'destinationPortCode', width: 180, colId: 'destinationPortCode' },
        { headerName: this.$t('SeaExport.CommissionedNumberOfPackages'), field: 'quantityContract', width: 180, colId: 'quantityContract' },
        { headerName: this.$t('queryJob.EntrustedWeight'), field: 'weightContract', width: 180, colId: 'weightContract' },
        { headerName: this.$t('SeaExport.CommissionedVolume'), field: 'cubageContract', width: 180, colId: 'cubageContract' },
        { headerName: this.$t('queryJob.NumberOfBookingPieces'), field: 'tempQuantity', width: 180, colId: 'tempQuantity', editable: this.isEdited, cellEditor: 'CargoAgGridCommonInputEditor' },
        { headerName: this.$t('queryJob.BookingWeight'), field: 'tempWeight', width: 180, colId: 'tempWeight', editable: this.isEdited, cellEditor: 'CargoAgGridCommonInputEditor' },
        { headerName: this.$t('queryJob.BookingVolume'), field: 'tempCubage', width: 180, colId: 'tempCubage', editable: this.isEdited, cellEditor: 'CargoAgGridCommonInputEditor' },
        { headerName: this.$t('Payment.remarks'), field: 'remark', width: 180, colId: 'remark' }
      ]
    }
  },
  watch: {
    visible: {
      async handler(newVal) {
        this.innerVisible = newVal
        if (newVal) {
          await this.init()
        }
      },
      immediate: true
    },
    innerVisible(newVal) {
      this.$emit('update:visible', newVal)
    }
  },
  methods: {
    async init() {
      let carrier = 0
      let flight = ''
      let etd = null
      let siteOp = 0
      if (this.jobs && this.jobs.length > 0) {
        for (const job of this.jobs) {
          if (!carrier) { carrier = job.carrierId }
          if (!flight) { flight = job.voyageFlight }
          if (!etd) { etd = job.etd }
          if (!siteOp) { siteOp = job.siteOp }
          job.tempWeight = (isNaN(job.weightBooking) || job.weightBooking === 0) ? job.weightContract : job.weightBooking
          job.tempQuantity = (isNaN(job.quantityBooking) || job.quantityBooking === 0) ? job.quantityContract : job.quantityBooking
          job.tempCubage = (isNaN(job.cubageBooking) || job.cubageBooking === 0) ? job.cubageContract : job.cubageBooking
        }
      }
      this.rowData = this.jobs
      if (siteOp) {
        const user = await User.getUserById(siteOp)
        if (user) {
          const dep = await new User(user).getDefaultDepartment('role', 'siteOp')
          if (dep) {
            this.form.siteOpDepartment = dep.departmentId
          }
        }
      } else {
        this.form.siteOpDepartment = 0
      }
      this.form.carrierId = carrier
      const company = await Company.getCompanyById(carrier)
      this.form.lbCarrier = company ? this.$getLangColumn(company) : '-'
      this.form.lbETD = etd ? DateUtils.customFormat(etd, 'YYYY-MM-DD') : '-'
      this.form.txtFlight = flight
      this.form.siteOp = siteOp
      this.savedPrint = false
    },
    async siteOpChangeHandler(data) {
      this.isEdited = true
      if (data) {
        this.form.siteOp = data.userId
        const dep = await new User(data).getDefaultDepartment('role', 'siteOp')
        if (dep) {
          this.form.siteOpDepartment = dep.departmentId
        } else {
          this.form.siteOpDepartment = 0
        }
      } else {
        this.form.siteOp = 0
        this.form.siteOpDepartment = 0
      }
    },
    async txtFlightChangeHandler() {
      this.isEdited = true
      if (this.form.siteOp) {
        const user = await User.getUserById(this.form.siteOp)
        if (user) {
          const dep = await new User(user).getDefaultDepartment('role', 'siteOp')
          if (dep) {
            this.form.siteOpDepartment = dep.departmentId
          } else {
            this.form.siteOpDepartment = 0
          }
        }
      } else {
        this.form.siteOpDepartment = 0
      }
    },
    getValueFromChild(obj) {
      this.form[obj.key] = obj.value
    },
    closeHandler() {
      this.innerVisible = false
    },
    sendSiteOp() {
      if (!this.form.txtFlight) {
        this.$message.warning(this.$t('queryJob.PleaseFillInTheFlight'))
        return
      }
      this.savedPrint = true
      this.saveJobs()
    },
    enterHandler() {
      if (this.isEdited) {
        this.savedPrint = true
        this.saveJobs()
      } else {
        this.print()
      }
    },
    saveJobs() {
      const jobs = []
      for (const job of this.rowData) {
        const item = {
          uuid: job.jobUuid,
          version: job.version,
          voyageFlight: this.form.txtFlight ? this.form.txtFlight : job.voyageFlight,
          siteOp: this.form.siteOp ? this.form.siteOp : 0,
          siteOpDepartment: this.form.siteOpDepartment ? this.form.siteOpDepartment : 0,
          weightBooking: isNaN(job.tempWeight) ? 0.0 : job.tempWeight,
          quantityBooking: isNaN(job.tempQuantity) ? 0 : job.tempQuantity,
          cubageBooking: isNaN(job.tempCubage) ? 0.0 : job.tempCubage
        }
        jobs.push(item)
        job.voyageFlight = this.form.txtFlight ? this.form.txtFlight : job.voyageFlight
        job.siteOp = this.form.siteOp ? this.form.siteOp : 0
        job.siteOpDepartment = this.form.siteOpDepartment ? this.form.siteOpDepartment : 0
      }
      if (jobs.length > 0) {
        sendSiteOp(jobs).then(res => {
          if (this.savedPrint) {
            this.print()
          }
          if (this.form.txtFlight && this.form.siteOp) {
            this.sendMail()
          }
          this.$emit('sendSiteOpCallBack', res.data)
        })
      }
    },
    print() {
      this.$emit('print', this.rowData)
      this.innerVisible = false
    },
    sendMail() {
      const user = this.$store.getters.userInfo
      sendXCOpEmail({ 'voyageFlight': this.form.txtFlight, 'opName': user.chineseName, 'opEmail': user.email, 'sendOp': this.form.siteOp }).then(res => {
        if (res.data && res.data === 1) {
          this.$message.warning(this.$t('Template.sendEmailSuccess'))
        }
      })
    }
  }
}
</script>

<style lang="scss">
</style>
